import React, { useCallback, useEffect, useState } from "react";
import client from "@culturehq/client";
import { Button, Icon, ModalDialog, TextField, Success, PlainButton } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch, useFieldsLabels } from "app/hooks";
import { t, Trans } from "app/utils";
import SelectTags from "components/post/SelectTags";
import StoryTrends from "components/landingPage/StoryTrends";
import tagsTypes from "constants/tagsTypes";
import ChqModalDialog from "components/common/ChqModalDialog";

const EMBBED_CODE = "<div id=\"IDENTIFIER\"></div><script type=\"text/javascript\">+function (c, h, q, z, y) { (y = document.createElement(\"script\")).type = \"text/javascript\"; y.src = \"https://assets.culturehq.com/WIDGET_VERSION\"; y.onload = function () { CHQ(c, h, q, z) }; document.getElementsByTagName(\"head\")[0].appendChild(y); }(\"#IDENTIFIER\", \"BOT_TOKEN\", FILTERS, \"EXTERNAL_ID\")</script>";

const replaceEmbbededCode = (orgId, filters, externalHash, widgetVersion) => EMBBED_CODE
  .replaceAll("IDENTIFIER", `chq_${Date.now()}`)
  .replace("BOT_TOKEN", orgId)
  .replace("WIDGET_VERSION", widgetVersion)
  .replace("FILTERS", JSON.stringify(filters))
  .replace("EXTERNAL_ID", externalHash);

const queryToOptions = queryString => {
  const { i, d, l, s, cl, cy, p, de, v, sp, org, t, co } = queryString;

  return {
    interestIds: i || null,
    companyIds: co || null,
    departmentIds: d || null,
    locationIds: l || null,
    skillIds: s || null,
    clubIds: cl || null,
    classYearIds: cy || null,
    programIds: p || null,
    degreeIds: de || null,
    organizationValueIds: v || null,
    storyPromptIds: sp || null,
    organizationId: org || null,
    trendIds: t || null
  };
};

const filtersToQuery = filters => {
  const options = {
    i: [],
    d: [],
    l: [],
    s: [],
    cl: [],
    cy: [],
    p: [],
    de: [],
    v: [],
    sp: [],
    t: [],
    co: []
  };

  filters.forEach(filter => {
    switch (filter.category) {
      case tagsTypes.COMPANIES:
        options.co.push(filter.value);
        break;
      case tagsTypes.INTERESTS:
        options.i.push(filter.value);
        break;
      case tagsTypes.SKILLS:
        options.s.push(filter.value);
        break;
      case tagsTypes.DEPARTMENTS:
        options.d.push(filter.value);
        break;
      case tagsTypes.LOCATIONS:
        options.l.push(filter.value);
        break;
      case tagsTypes.CLUBS:
        options.cl.push(filter.value);
        break;
      case tagsTypes.CLASS_YEARS:
        options.cy.push(filter.value);
        break;
      case tagsTypes.PROGRAMS:
        options.p.push(filter.value);
        break;
      case tagsTypes.DEGREES:
        options.de.push(filter.value);
        break;
      case tagsTypes.ORG_VALUES:
        options.v.push(filter.value);
        break;
      case tagsTypes.USERS:
        options.u.push(filter.value);
        break;
      case tagsTypes.STORY_PROMPTS:
      case tagsTypes.STORY_TRENDS:
        options.t.push(filter.value);
        break;
      default:
        break;
    }
  });

  return options;
};

const EmbedStoryTrendsSlider = ({
  onClose: onCloseMenu, orgName, botToken
}) => {
  const { profile } = useProfile();
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [embeddableCode, setEmbeddableCode] = useState();
  const [externalHash] = useState(`#chq_${Date.now()}`);
  const [widgetVersion] = useState(
    orgName === "activisionblizzard" ? "act_widget_trends_1662499689.js" : "widget_trends_1657874665.js"
  );

  const labels = useFieldsLabels();
  const { json: { storyQuestions } } = useFetch(
    () => client.getStoryQuestions({ storyTrends: 1 }),
    []
  );

  useEffect(
    () => {
      const query = filtersToQuery(selectedFilters);
      setEmbeddableCode(replaceEmbbededCode(
        botToken, { ...query, org: profile.organization.id }, externalHash, widgetVersion
      ));
    }, [botToken, profile, selectedFilters, externalHash, widgetVersion]
  );

  const onOpen = useCallback(() => setOpen(true), [setOpen]);
  const onClose = useCallback(
    () => {
      setCopied(false);
      setOpen(false);
      setSelectedFilters([]);
      if (onCloseMenu) {
        onCloseMenu();
      }
    },
    [onCloseMenu, setCopied, setOpen]
  );

  const onCopy = useCallback(
    () => {
      const selector = "#embeddable_code";
      document.querySelector(selector).select();
      document.execCommand("copy");
      setCopied(true);
    },
    [setCopied]
  );

  const handleSelectedTags = (updatedOptions, _) => {
    setSelectedFilters(updatedOptions);
  };

  const handleClearSelectedOptions = () => {
    setSelectedFilters([]);
  };

  return (
    <>
      <PlainButton onClick={onOpen}>
        <Icon icon="layout" /><Trans>Story Trend Slider</Trans>
      </PlainButton>
      {open && (
        <ChqModalDialog onClose={onClose} className="EmbedStories">
          <ModalDialog.Heading onClose={onClose}>
            <Trans>Embed Story Trends in a Carousel Slider</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <SelectTags
              allWordsMatch
              ariaLabel={
                t(`Filter stories by tag, ${labels.departments.singular.toLowerCase()}, location and/or trend`)
              }
              creatable={false}
              placeholder={
                t(`Filter stories by tag, ${labels.departments.singular.toLowerCase()}, location and/or trend`)
              }
              selectedOptions={selectedFilters}
              onShareWithChanged={handleSelectedTags}
              isSearchBar
              onClearSelectedOptions={handleClearSelectedOptions}
              withOrgValues
              storyQuestions={storyQuestions}
            />
            <StoryTrends
              queryParams={queryToOptions(filtersToQuery(selectedFilters))}
              organization={profile.organization.name}
              admin
            />
            <TextField
              readOnly
              rows={3}
              name="embeddable_code"
              value={embeddableCode}
            />
            {copied && (
              <Success>
                <Trans>Embeddable code copied to the clipboard.</Trans>
              </Success>
            )}
            <div className="ButtonContainer">
              <Button icon="copy" primary onClick={onCopy}>
                <Trans>Copy Code</Trans>
              </Button>
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default EmbedStoryTrendsSlider;
